import cloneDeep from "lodash-es/cloneDeep";
import compact from "lodash-es/compact";
import difference from "lodash-es/difference";
import isNumber from "lodash-es/isNumber";
import without from "lodash-es/without";
import { parse, stringify } from "query-string";

import { ITEMS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import { hashCode64 } from "@/mk2/helpers/hashes";
import { url } from "@/mk2/helpers/urls";
import {
    GridSort,
    MyGridFilter,
    MyGridSort,
    Quality,
    Settings,
    WishlistSort,
} from "@/mk2/reducers/request";
import { COMMON_PLACES_ABROAD, PAGE_SLUG } from "@/mk/autogenerated/translation";
import {
    MY_FILTER_VALUES,
    MY_SORTING_VALUES,
    QUALITY_VALUES,
    SORTING_VALUES,
    WISHLIST_SORTING_VALUES,
} from "@/mk/bazaar/common/enums";
import { canReportProducts, canUseCleanSort } from "@/mk/bazaar/common/userUtils";
import { dictBySlug, getPropertiesForCategories } from "@/mk/bazaar/product/categories";
import {
    Filter,
    defaultFilter,
    deserializeFilterFromPathname,
    serializeFilterForPathname,
    serializeFilterForQuery,
    setCategory,
    setSettings,
    setUser,
} from "@/mk/bazaar/product/filter/filterUtils";
import { getCountyId } from "@/mk/geo";

interface Params {
    username?: string;
    category?: string;
    filters?: string;
    page?: string;
}

/**
 * Deserialize filter from Match object
 */
export function deserializeFilter(
    params: Params,
    search: string,
    settings: Settings,
    requestUserPermissions: string[],
): Filter {
    let filter = cloneDeep(defaultFilter());
    if (settings) {
        filter = setSettings(filter, settings);
    }

    let categoryParam;
    let filterParam;
    let pageParam;

    if (params.category && params.category.startsWith(`${PAGE_SLUG}-`)) {
        categoryParam = null;
        filterParam = null;
        pageParam = params.category;
    } else if (params.filters && params.filters.startsWith(`${PAGE_SLUG}-`)) {
        categoryParam = params.category;
        filterParam = null;
        pageParam = params.filters;
    } else {
        categoryParam = params.category;
        filterParam = params.filters;
        pageParam = params.page;
    }

    if (categoryParam) {
        const cat = dictBySlug()[categoryParam];
        if (!cat) {
            return null;
        }
        filter = setCategory(filter, cat.name);

        if (filterParam && cat.pathnameFilters) {
            try {
                const pathnameFilter = deserializeFilterFromPathname(cat, filterParam);
                filter.locations = pathnameFilter.locations;
                filter.props = {
                    ...filter.props,
                    ...pathnameFilter.props,
                };
            } catch (err) {
                // Something is wrong when deserializing filter from pathname - return 404
                return null;
            }
        }
    }

    if (params.username) {
        filter = setUser(filter, params.username);
    }

    const query = parse(search) || {};
    const {
        locations,
        places,
        q,
        price_lo,
        price_hi,
        sellers,
        cleanSort,
        quality,
        myGridSort,
        myGridFilter,
        complaints,
        notMarked,
        wishlistSort,
        page,
        placesRadius,
        valid,
    } = query;
    const gridSort = query.sort;

    // Now fill filter with values from qs
    // locations
    if (locations) {
        // _.compact will clear all undefined values
        filter.locations = (locations as string)
            .split(",")
            .map((location) => {
                return location === COMMON_PLACES_ABROAD ? 0 : getCountyId(location);
            })
            .filter((v) => v !== undefined && v !== null);
    }

    // places
    if (places) {
        // _.compact will clear all undefined values
        filter.places = compact(
            (places as string).split(",").map((place) => {
                return parseInt(place, 10);
            }),
        );
    }

    // q
    if (q) {
        filter.q = q as string;
    }

    // price_lo
    if (price_lo !== undefined && !isNaN(parseFloat(price_lo as string))) {
        filter.price_lo = parseFloat(price_lo as string);
    }

    // price_hi
    if (price_hi !== undefined && !isNaN(parseFloat(price_hi as string))) {
        filter.price_hi = parseFloat(price_hi as string);
    }

    // sellers
    if (sellers !== undefined) {
        // Restrict to only 'all' and 'liked' values
        filter.sellers = sellers === "all" || sellers === "liked" ? sellers : "all";
    }

    if (valid === "true") {
        filter.valid = true;
    } else if (valid === "false") {
        filter.valid = false;
    }

    // props
    // All props are uppercase, so that's our criteria to decide
    const propsForCat = getPropertiesForCategories(filter.cats);
    const props = Object.keys(query).filter((prop) => {
        return prop.toUpperCase() === prop && propsForCat.indexOf(`p_${prop.toLowerCase()}`) !== -1;
    });

    try {
        props.forEach((prop) => {
            // This will skip URLs like "?BRAND", "?quality=old&"
            if (query[prop] === null) {
                return;
            }
            if (Array.isArray(query[prop])) {
                // throw 404 for ?SIZE=52&SIZE=54&SIZE=55
                // We only support ?SIZE=52,54,55 (no SEO duplicate content)
                throw new Error("There shouldn't be more that one property with same name in URL");
            }
            filter.props[`p_${prop.toLowerCase()}`] = (query[prop] as string).split(",");
        });
    } catch (e) {
        return null;
    }

    // settings
    // gridSort
    if (gridSort) {
        let newGridSort = gridSort;
        let gridSortValues = Object.keys(SORTING_VALUES);
        if (!filter.q) {
            gridSortValues = without(gridSortValues, "score_new");
            if (newGridSort === "score_new") {
                newGridSort = settings.gridSort;
            }
        }
        if (!canReportProducts(requestUserPermissions)) {
            gridSortValues = without(
                gridSortValues,
                "complaints",
                "complaints_asc",
                "complaint_time",
                "complaint_time_asc",
            );
        }

        if (gridSortValues.indexOf(newGridSort as string) === -1) {
            newGridSort = gridSortValues[0];
        }
        filter.settings.gridSort = newGridSort as GridSort;
    }

    // wishlist sort
    if (wishlistSort) {
        let newWishlistSort = wishlistSort;
        const sortValues = Object.keys(WISHLIST_SORTING_VALUES);
        if (sortValues.indexOf(newWishlistSort as string) === -1) {
            newWishlistSort = sortValues[0];
        }
        filter.settings.wishlistSort = newWishlistSort as WishlistSort;
    }

    // cleanSort
    if (cleanSort && canUseCleanSort(requestUserPermissions)) {
        filter.settings.cleanSort = cleanSort === "true";
    }

    if (placesRadius) {
        filter.settings.placesRadius = placesRadius === "true";
    }

    const qualityValues = Object.keys(QUALITY_VALUES);

    // quality
    if (quality) {
        // backward compatibility
        if (quality === "all") {
            filter.settings.quality = qualityValues as Quality[];
        } else {
            const qualityArray = (quality as string).split(",");
            filter.settings.quality = (
                difference(qualityArray, qualityValues).length ? qualityValues : qualityArray
            ) as Quality[];
        }
    }

    // myGridSort
    if (myGridSort) {
        const myGridSortValues = Object.keys(MY_SORTING_VALUES);
        filter.settings.myGridSort = (
            myGridSortValues.indexOf(myGridSort as string) === -1 ? myGridSortValues[0] : myGridSort
        ) as MyGridSort;
    }

    // myGridFilter
    if (myGridFilter) {
        const myGridFilterValues = Object.keys(MY_FILTER_VALUES);
        filter.settings.myGridFilter = (
            myGridFilterValues.indexOf(myGridFilter as string) === -1
                ? myGridFilterValues[0]
                : myGridFilter
        ) as MyGridFilter;
    }

    if (complaints) {
        filter.complaints = true;
    }

    if (notMarked) {
        filter.notMarked = true;
    }

    if (pageParam) {
        filter.page = parseInt(pageParam.replace(`${PAGE_SLUG}-`, ""), 10);
    } else if (page) {
        filter.page = parseInt(page as string, 10);
    } else {
        filter.page = 1;
    }

    if (isNaN(filter.page)) {
        // Unknown page - break the page and display 404
        return null;
    }

    return filter;
}

export function hashFilter(filter: Filter): string {
    if (!filter) {
        return null;
    }

    let value = `cats:${filter.cats.join(",")}`;
    if (filter.page) {
        value += `start:${(filter.page - 1) * ITEMS_PER_PAGE}`;
    } else if (isNumber(filter.start)) {
        value += `start:${filter.start}`;
    }
    value += `pathname:${serializeFilterForPathname(filter) || ""}`;
    value += `search:${stringify(serializeFilterForQuery(filter, false))}`;

    return hashCode64(value).toString(16);
}

export function watchdogKey(filter: Filter, path: string, params: Params): string {
    const clonedFilter: Filter = {
        ...filter,
        settings: {
            ...filter.settings,
            cleanSort: true,
            gridSort: "watchdog",
        },
        page: undefined,
    };

    const query = stringify(serializeFilterForQuery(clonedFilter));
    const pathname = url(path, {
        ...params,
        page: undefined,
    });

    return `${pathname}?${query}`;
}
